import { FormElementComponent } from "@/components/forms/components/FormElementComponent.tsx";
import { HighlightDroppable } from "@/components/forms/components/HighlightDroppable.tsx";
import { useFormEditor } from "@/components/forms/form-editor-context.ts";
import { useFormElementUtils } from "@/components/forms/utils/useFormElementUtils.tsx";
import { Stack, Title } from "@skailer-com/uikit";
import { Draggable } from "@hello-pangea/dnd";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

interface FormSectionComponentProps {
  pageIndex: number;
  sectionIndex: number;
}

export const FormSectionComponent = ({ pageIndex, sectionIndex }: FormSectionComponentProps) => {
  const { t } = useTranslation();
  const { formValues, readOnly } = useFormEditor();
  const { renderEditBlock } = useFormElementUtils();

  const section = formValues.pages[pageIndex].sections[sectionIndex];

  if (formValues.pages[pageIndex].sections.length <= 1 && formValues.pages.length <= 1) {
    return (
      <HighlightDroppable droppableId={`${pageIndex}-${sectionIndex}`} type="element">
        {(provided) => (
          <Stack gap={"lg"} {...provided.droppableProps} ref={provided.innerRef}>
            {section.elements.map((_, elementIndex) => (
              <FormElementComponent
                key={`element-${pageIndex}-${sectionIndex}-${elementIndex}`}
                pageIndex={pageIndex}
                sectionIndex={sectionIndex}
                elementIndex={elementIndex}
              />
            ))}
          </Stack>
        )}
      </HighlightDroppable>
    );
  }

  return (
    <Draggable
      key={`section-${pageIndex}-${sectionIndex}`}
      draggableId={`section-${pageIndex}-${sectionIndex}`}
      index={sectionIndex}
      isDragDisabled={readOnly}
    >
      {(provided) => (
        <Stack ref={provided.innerRef} {...provided.draggableProps}>
          <Fragment>
            <Stack
              direction={"horizontal"}
              gap={"lg"}
              className={twMerge(
                "group",
                section.elements.length > 0 ? "pb-(--gap-xl)" : undefined
              )}
            >
              {renderEditBlock(pageIndex, sectionIndex, undefined, provided.dragHandleProps)}
              <Stack className={"flex-1"}>
                <Title size={"xs"} header={t("common:section")} />
                <Title
                  header={section.title || t("common:no_title")}
                  className={section.title ? "" : "text-secondary"}
                  size={"lg"}
                />
              </Stack>
            </Stack>

            <HighlightDroppable droppableId={`${pageIndex}-${sectionIndex}`} type="element">
              {(provided) => (
                <Stack gap={"lg"} {...provided.droppableProps} ref={provided.innerRef}>
                  {section.elements.map((_, elementIndex) => (
                    <FormElementComponent
                      key={`element-${pageIndex}-${sectionIndex}-${elementIndex}`}
                      pageIndex={pageIndex}
                      sectionIndex={sectionIndex}
                      elementIndex={elementIndex}
                    />
                  ))}
                  {provided.placeholder}
                </Stack>
              )}
            </HighlightDroppable>
          </Fragment>
        </Stack>
      )}
    </Draggable>
  );
};
